const { NODE_ENV } = process.env

export const isDevelopment = () => NODE_ENV === 'development'

export const isProduction = () => NODE_ENV === 'production'

export const BREAKPOINTS = {
  TABLET_SMALL: 768,
  TABLET_LANDSCAPE: 992,
}

const properties = [
  'mileTime',
  'pushUps',
  'squats',
  'sitUps',
  'bodyWeight',
  'muscleMass',
  'bodyFat',
]

export const statFormLimits = {
  bodyWeight: {
    min: 0,
    max: 999,
    maxDigits: 5,
  },
  minutes: {
    min: 3,
    max: 59,
    maxDigits: 2,
  },
  seconds: {
    min: 0,
    max: 59,
    maxDigits: 2,
  },
  bodyFat: {
    min: 1,
    max: 99,
    maxDigits: 2,
  },
  muscleMass: {
    min: 1,
    max: 99,
    maxDigits: 2,
  },
  squats: {
    min: 1,
    max: 199,
    maxDigits: 3,
  },
  sitUps: {
    min: 1,
    max: 199,
    maxDigits: 3,
  },
  pushUps: {
    min: 1,
    max: 199,
    maxDigits: 3,
  },
}

export const numberRegex = /^[0-9][.\d]*(,\d+)?$/
const floatRegex = /^\d+(\.\d{1})?$/
const integerRegex = /^\d+$/

export const getDigitValidation = ({ id, value, maxLength }) => {
  let validationResult = false
  const floatFields = ['bodyWeight', 'bodyFat', 'muscleMass']
  const inputString = value.toString()
  if (floatFields.includes(id)) {
    validationResult = floatRegex.test(inputString)
  } else {
    validationResult = integerRegex.test(inputString)
  }
  return validationResult
}

export const errorMessage = "Hm, this doesn't look right."

export const getTimeProgress = (recent, first) => {
  const progress = recent > first ? recent - first : first - recent
  const progressMm = Math.floor(progress / 60)
  const progressSs = progress - progressMm * 60
  return `${
    recent !== first ? (recent > first ? '+' : '-') : ''
  }${progressMm}:${progressSs.toString().length === 1 ? '0' : ''}${progressSs}`
}

export const KEY_CODES = {
  CHROME_MINUS: 189,
  DOT: 190,
  EXPONENTIAL: 69,
  MOZ_MINUS: 173,
}

export const formatTime = (min = 0, sec = 0) => {
  if (min?.toString().length || sec?.toString().length) {
    const minutes = min?.toString().length ? min : 0
    const seconds = sec?.toString().length ? sec : 0
    return parseInt(minutes) * 60 + parseInt(seconds)
  } else {
    return null
  }
}

export const STATS_SUBMISSION_ERROR =
  'Oops, something went wrong! Please try submitting your stats again!'

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const isEmailRegEx = (value) => EMAIL_REGEX.test(value)

export const LOGIN_ERROR =
  'There was an issue logging you in. Please try again!'

export const addKey = (obj, key) => {
  return obj[key].map(({ value, ...rest }) => ({
    ...rest,
    [key]: value,
  }))
}

export const groupBy = (arr, key) =>
  arr
    .map((val) => val[key])
    .reduce((group, val, i) => {
      group[val] = { ...group[val], ...arr[i] }
      return group
    }, {})

export const sort = (arr, key) => arr.sort((a, b) => a[key] - b[key])

export const config = process.env

export const options = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
}

export const unknownError = 'An unknown error occured'

export const formatNumber = (number) => (+number <= 9 ? `0${number}` : number)

export const parseDateString = (dateString) =>
  new Date(dateString.replace(/ /g, 'T'))

export const formatDateDDMM = (date) => {
  const inputDate = new Date(date)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const options = {
    day: '2-digit',
    month: '2-digit',
    timeZone: userTimeZone,
  }
  return new Intl.DateTimeFormat('en-US', options).format(inputDate)
}

const convertSecondsToTimeFormat = (time) => {
  const minutes = Math.floor(time / 60)
  const seconds = time % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

export const convertMinAndSecToSec = ({ mileTimeMin, mileTimeSec }) =>
  +mileTimeMin * 60 + +mileTimeSec

export const combineMetricsFromChallenges = (challenges) =>
  challenges.reduce((acc, challenge) => {
    const { cgFitMetricsList } = challenge
    if (cgFitMetricsList?.length) {
      acc.push(...cgFitMetricsList)
    }
    return acc
  }, [])

export const parseMetricsForHistoryTable = (metrics) =>
  metrics
    .sort(
      (a, b) => parseDateString(b.metricDate) - parseDateString(a.metricDate),
    )
    .map(
      ({
        metricDate,
        bodyWeight,
        bodyFat,
        squats,
        sitUps,
        pushUps,
        mileTime,
        muscleMass,
      }) => ({
        Date: formatDateDDMM(metricDate),
        Weight: bodyWeight ? `${formatNumber(bodyWeight)} lbs` : '--',
        'Body Fat': bodyFat ? `${formatNumber(bodyFat)}%` : '--',
        'Muscle Mass': muscleMass ? formatNumber(muscleMass) : '--',
        Squats: squats ? formatNumber(squats) : '--',
        'Sit Ups': sitUps ? formatNumber(sitUps) : '--',
        'Push Ups': pushUps ? formatNumber(pushUps) : '--',
        'Mile Time': mileTime ? convertSecondsToTimeFormat(mileTime) : '--',
      }),
    )

export const findMostRecentSubmission = (stats) => {
  const allKeys = [...new Set(stats.flatMap(Object.keys))]

  const recentSubmission = stats.reduce((acc, stat) => {
    if (!acc) return stat
    return parseDateString(stat.metricDate) > parseDateString(acc.metricDate)
      ? stat
      : acc
  }, null)

  const result = allKeys.reduce((acc, key) => {
    acc[key] =
      recentSubmission[key] !== undefined ? recentSubmission[key] : null
    return acc
  }, {})

  for (const key in result) {
    if (result[key] === null || result[key] === undefined) {
      const mostRecentWithValue = stats
        .filter((stat) => stat[key] !== null && stat[key] !== undefined)
        .reduce((acc, stat) => {
          if (!acc) return stat
          return parseDateString(stat.metricDate) >
            parseDateString(acc.metricDate)
            ? stat
            : acc
        }, null)
      result[key] = mostRecentWithValue ? mostRecentWithValue[key] : null
    }
    if (key === 'mileTime' && result[key] !== null) {
      result[key] = convertSecondsToTimeFormat(result[key])
    }
  }
  return result
}

const getMostRecentWithValue = ({ properties, newestStats, oldestStats }) =>
  properties.reduce((acc, property) => {
    const newestValue = newestStats[property]
    const oldestValue = oldestStats[property]

    if (
      newestValue === null ||
      newestValue === undefined ||
      oldestValue === null ||
      oldestValue === undefined
    ) {
      acc[property] = null
    } else {
      acc[property] = newestValue - oldestValue
      acc[property] = Math.round(acc[property] * 100) / 100
    }

    return acc
  }, {})

const getNewestAndOldestStats = (stats) => {
  const statsSortedByDate = stats.sort(
    (a, b) => parseDateString(b.metricDate) - parseDateString(a.metricDate),
  )
  const newestStats = statsSortedByDate[0]
  const oldestStats = statsSortedByDate[statsSortedByDate.length - 1]

  return { newestStats, oldestStats }
}

export const calculateStatDifferences = (stats) => {
  const { newestStats, oldestStats } = getNewestAndOldestStats(stats)

  const differences = getMostRecentWithValue({
    properties,
    newestStats,
    oldestStats,
  })

  return differences
}

export const preventNonNumericalInput = (e) => {
  const charCode = typeof e.which === 'undefined' ? e.keyCode : e.which
  const charStr = String.fromCharCode(charCode)
  if (!charStr.match(numberRegex) && charStr !== '.' && e.key !== 'Enter') {
    e.preventDefault()
  }
}

export const findCurrentlyActiveChallenge = (challenges) =>
  challenges.find(({ type, startDate, endDate }) => {
    const today = new Date()
    const challengeStartDate = new Date(startDate)
    const challengeEndDate = new Date(endDate)
    return (
      type === 'CG_FIT' &&
      today >= challengeStartDate &&
      today <= challengeEndDate
    )
  })
